import React, { useState, useEffect } from "react";
import { Table, Tag, Button, Modal, Form, Input, Select, Space, App } from "antd";
import { EditOutlined, FilterOutlined, ExclamationCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchWithdraws, updateWithdrawStatus } from "../../store/slices/withdrawSlice";
import dayjs from "dayjs";
import ExportButton from "../../Reusable/ExportButton/ExportButton";
import Loadingpage from "../Loadingpage/Loadingpage";


const Withdraw = () => {

  const { modal, message } = App.useApp();
  const dispatch = useDispatch();
  const { withdraws, total, loading } = useSelector((state) => state.withdraw);

  const [page, setPage] = useState({ current: 1, pageSize: 10 });
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    search: ''
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWithdraw, setSelectedWithdraw] = useState(null);
  const [form] = Form.useForm();
  const watchStatus = Form.useWatch('status', form);
  const [searchTrigger, setSearchTrigger] = useState("");

  useEffect(() => {
    dispatch(fetchWithdraws({
      page: page.current,
      limit: page.pageSize,
      status: filters.status !== 'all' ? filters.status : undefined,
      type: filters.type !== 'all' ? filters.type : undefined,
      search: searchTrigger || undefined
    }));
  }, [dispatch, page.current, page.pageSize, filters.status, filters.type, searchTrigger]);
  const handleUpdateStatus = async (values) => {
    try {
      await dispatch(updateWithdrawStatus({
        id: selectedWithdraw._id,
        data: {
          status: values.status,
          remark: values.remark || ""
        }
      })).unwrap();

      message.success(`Withdraw ${values.status} successfully`);
      setIsModalOpen(false);
      form.resetFields();
      dispatch(fetchWithdraws({
        page: page.current,
        limit: page.pageSize,
        ...filters
      }));
    } catch (err) {
      const errorMsg = typeof err === 'string' ? err : err?.message || 'Failed to update withdraw';
      message.error(errorMsg);
    }
  };

  const showConfirm = (values) => {
    modal.confirm({
      title: `Are you sure you want to ${values.status} this withdrawal?`,
      icon: <ExclamationCircleOutlined />,
      content: `User will be notified of the ${values.status} status.`,
      okText: 'Yes',
      cancelText: 'No',
      centered: true,
      onOk: () => handleUpdateStatus(values)
    });
  };

  const columns = [
    {
      title: "Sno",
      render: (_, __, i) => <span style={{ color: '#fff' }}>{(page.current - 1) * page.pageSize + i + 1}</span>
    },
    {
      title: "User",
      dataIndex: ["user_id", "email"],
      key: "user",
      render: (email) => <span style={{ color: '#fff' }}>{email || '-'}</span>
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag color={type === 'crypto' ? '#FC4420' : '#108ee9'} style={{ borderRadius: '4px' }}>
          {type?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Currency/Method",
      key: "currency",
      render: (_, record) => (
        <span style={{ color: '#fff' }}>
          {record.type === 'crypto'
            ? record.crypto_currency_id?.name
            : record.fiat_method_id?.type?.toUpperCase()}
        </span>
      )
    },
    {
      title: "Network",
      dataIndex: ["network_id", "network"],
      key: "network",
      render: (network, record) => (
        <span style={{ color: '#fff' }}>
          {record.type === 'crypto' ? (network || '-') : 'N/A'}
        </span>
      )
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount, record) => (
        <span style={{ color: '#fff', fontWeight: 'bold' }}>
          {amount} {record.type === 'crypto' ? record.currency_id?.name : 'USD'}
        </span>
      )
    },
    {
      title: "Address / Details",
      key: "details",
      render: (_, record) => (
        <div style={{ color: '#fff', fontSize: '12px' }}>
          {record.type === 'crypto' ? (
            <span style={{ wordBreak: 'break-all' }}>{record.address}</span>
          ) : (
            <div>
              {record.account_details?.upi_id && <div>UPI: {record.account_details.upi_id}</div>}
              {record.account_details?.account_number && (
                <>
                  <div>Name: {record.account_details.account_name}</div>
                  <div>A/C: {record.account_details.account_number}</div>
                  {/* <div>Bank: {record.account_details.bank_name}</div> */}
                  <div>IFSC: {record.account_details.ifsc}</div>
                </>
              )}
            </div>
          )}
        </div>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === 'approved' ? 'green' : status === 'pending' ? 'gold' : 'red'}>
          {status?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => <span style={{ color: '#fff' }}>{dayjs(date).format('DD-MM-YYYY HH:mm')}</span>
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          disabled={record.status !== 'pending'}
          onClick={() => {
            setSelectedWithdraw(record);
            form.setFieldsValue({ status: 'approved' });
            setIsModalOpen(true);
          }}
          style={{ color: record.status === 'pending' ? '#FC4420' : 'rgba(255, 255, 255, 0.3)' }}
        >
          Process
        </Button>
      )
    }
  ];

  const clearFilters = () => {
    setFilters({
      status: 'all',
      type: 'all',
      search: ''
    });

    setSearchTrigger("");

    setPage({
      current: 1,
      pageSize: 10
    });
  };

  return (
    <div className="p-20">
      <Loadingpage />
      <div className="card-clr" style={{ padding: '20px', borderRadius: '16px', border: '1px solid #FC442080' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: '#fff', margin: 0 }}>Withdrawal Management</h2>

        </div>
        <div style={{ marginBottom: 16,display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap',gap: 10 }}>
            <Input.Search
              placeholder="Search Crypto Address"
              value={filters.search}
              onChange={(e) => {
                const value = e.target.value;

                setFilters((f) => ({
                  ...f,
                  search: value,
                }));

                // clear icon click
                if (!value) {
                  setSearchTrigger("");

                  setPage({
                    current: 1,
                    pageSize: 10,
                  });
                }
              }}
              onSearch={(v) => {
                setSearchTrigger(v);

                setPage({
                  current: 1,
                  pageSize: 10,
                });
              }}
              allowClear
              // value={filters.search}
              // onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              style={{ width: 250 }}
              className="input-cls"
            />
            <Select
              value={filters.status}
              style={{ width: 140 }}
              onChange={(v) => {
                setFilters(f => ({ ...f, status: v }));
                setPage({ current: 1, pageSize: 10 });
              }}
              className="selected-cls"
            >
              <Select.Option value="all">All Status</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="approved">Approved</Select.Option>
              <Select.Option value="rejected">Rejected</Select.Option>
            </Select>
            <Select
              value={filters.type}
              style={{ width: 140 }}
              onChange={(v) => {
                setFilters(f => ({ ...f, type: v }));
                setPage({ current: 1, pageSize: 10 });
              }}
              className="selected-cls"
            >
              <Select.Option value="all">All Types</Select.Option>
              <Select.Option value="crypto">Crypto</Select.Option>
              <Select.Option value="fiat">Fiat</Select.Option>
            </Select>

            <Button icon={<CloseCircleOutlined />} onClick={clearFilters}>Clear</Button>
            <ExportButton
              filters={{
                status: filters.status !== 'all' ? filters.status : '',
                type: filters.type !== 'all' ? filters.type : '',
                search: filters.search !== '' ? filters.search : ''
              }}
              category="withdrawals"
            />
        </div>

        <Table
          columns={columns}
          dataSource={withdraws}
          loading={{
            spinning: loading,
            indicator: <Loadingpage isLoading={loading} />,
          }}
          pagination={{
            total: total,
            current: page.current,
            pageSize: page.pageSize,
            onChange: (p, s) => setPage({ current: p, pageSize: s }),
            className: "pagination-clr"
          }}
          rowKey="_id"
          className="table-clr"
          scroll={{ x: 'max-content' }}
        />

        <Modal
          title="Process Withdrawal"
          open={isModalOpen}
          onCancel={() => {
            setIsModalOpen(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          className="custom-modal"
          centered
        >
          <Form form={form} layout="vertical" onFinish={showConfirm}>
            <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(252, 68, 32, 0.1)', borderRadius: '8px' }}>
              <div style={{ color: '#fff', marginBottom: '4px' }}>
                Type: <Tag color={selectedWithdraw?.type === 'crypto' ? '#FC4420' : '#108ee9'}>{selectedWithdraw?.type?.toUpperCase()}</Tag>
              </div>
              <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
                Amount: {selectedWithdraw?.amount} {selectedWithdraw?.type === 'crypto' ? selectedWithdraw?.crypto_currency_id?.symbol : 'USD'}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginTop: '8px' }}>
                Destination: {selectedWithdraw?.type === 'crypto' ? selectedWithdraw?.address : 'Bank/UPI (See table for details)'}
              </div>
            </div>

            <Form.Item name="status" label="Action" rules={[{ required: true }]}>
              <Select>
                <Select.Option value="approved">Approve</Select.Option>
                <Select.Option value="rejected">Reject</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item name="remark" label="Admin Remark">
              <Input.TextArea placeholder="Enter any notes or rejection reason" rows={4} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default Withdraw;
